// PipelineSwitch — the content pipeline's master switch, always in the header.
//
// Reads app_config `studio_agents_enabled` (the historical key name) and flips
// it through studio-toggle-master-switch, which checks admin and writes an
// audit row. Off, nothing is planned or finished and the pipeline's cron jobs
// return immediately; the budget breaker also turns it off. Photos has its
// own switch (Photos → Limits).
//
// Paused is a deliberate state, not a failure: it uses the warning recipe,
// never danger, and never pulses.

import { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Icon, Power } from '@/components/Icon';
import { configValue, useAppConfig } from '@/data/queries/appConfig';
import { useToggleMasterSwitch } from '@/data/mutations/toggleMasterSwitch';

const KEY = 'studio_agents_enabled';

const PAUSE_BODY =
  'Nothing new is planned, generated or finished until you resume: the 23:30 UTC daily plan and the 15-minute finish sweep return immediately. Cards already published stay live. Photos is not affected; it has its own switch.';
const RESUME_BODY =
  'The daily plan and the finish sweep run again on their next scheduled tick. Use Videos → Pipeline → “Plan now” if you need a day’s cards before 23:30 UTC.';

export function PipelineSwitch(): JSX.Element {
  const { data, isLoading } = useAppConfig([KEY]);
  const toggle = useToggleMasterSwitch();
  const [confirming, setConfirming] = useState(false);

  const enabled = configValue.bool(data?.[KEY], true);

  const triggerClass = enabled
    ? 'border border-border-default bg-surface-2 text-fg-default hover:bg-surface-3'
    : 'border border-warning-6 bg-warning-3 text-warning-12 hover:bg-warning-6/40';

  return (
    <AlertDialog.Root open={confirming} onOpenChange={setConfirming}>
      <AlertDialog.Trigger asChild>
        <button
          type="button"
          disabled={isLoading || toggle.isPending}
          aria-label={
            isLoading
              ? 'Content pipeline: loading'
              : enabled
                ? 'Content pipeline on. Pause it'
                : 'Content pipeline paused. Resume it'
          }
          data-testid="pipeline-switch"
          data-state={enabled ? 'on' : 'off'}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${triggerClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50`}
        >
          <Icon icon={Power} size={16} />
          <span>{isLoading ? 'Pipeline…' : enabled ? 'Pipeline on' : 'Pipeline paused'}</span>
        </button>
      </AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-md" />
        <AlertDialog.Content
          data-testid="pipeline-switch-confirm"
          className="fixed left-1/2 top-1/2 z-50 w-[min(32rem,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border-subtle bg-surface-3 p-6 shadow-modal"
        >
          <AlertDialog.Title className="text-md font-semibold text-fg-default">
            {enabled ? 'Pause the content pipeline?' : 'Resume the content pipeline?'}
          </AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-sm text-fg-muted">
            {enabled ? PAUSE_BODY : RESUME_BODY}
          </AlertDialog.Description>
          <div className="mt-4 flex justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-fg-default hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                type="button"
                onClick={() => {
                  void toggle.mutateAsync({ enabled: !enabled });
                  setConfirming(false);
                }}
                className={
                  enabled
                    ? 'rounded-md border border-warning-6 bg-warning-3 px-3 py-1.5 text-sm font-medium text-warning-12 hover:bg-warning-6/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'
                    : 'rounded-md border border-success-6 bg-success-3 px-3 py-1.5 text-sm font-medium text-success-12 hover:bg-success-6/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'
                }
              >
                {enabled ? 'Pause pipeline' : 'Resume pipeline'}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
